'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Trash2, Package, CheckCircle2, ArrowLeft } from 'lucide-react';
import { QuantitySelector } from '@/components/product/quantity-selector';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useCart } from '@/hooks/useCart';
import { formatPrice } from '@/lib/utils';

interface OrderPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Cart drawer (V1 Telegram lifecycle): items + total + Checkout navigation
 * only. The contact/address form lives on the dedicated /checkout page —
 * never embedded here — so the drawer stays readable on phone and desktop.
 */
export function OrderPanel({ isOpen, onClose }: OrderPanelProps) {
  const { items, itemCount, total, removeItem, updateQuantity } = useCart();

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b border-border">
          <div className="flex items-center justify-between pe-8">
            <div className="text-start">
              <SheetTitle className="text-xl font-extrabold">قائمة الطلبات</SheetTitle>
              <p className="text-xs font-semibold text-muted-foreground">{itemCount} عنصر</p>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
                <Package className="h-8 w-8" />
              </div>
              <p className="text-base font-bold text-muted-foreground">قائمة طلباتك فارغة</p>
              <p className="mt-1 text-xs text-muted-foreground">تصفح الهواتف وأضف أجهزتك المفضلة</p>
            </div>
          ) : (
            items.map((item) => (
              <div
                key={`${item.productId}-${item.variantId}`}
                className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3.5"
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeItem(item.productId, item.variantId)}
                  className="text-muted-foreground hover:text-destructive"
                  title="حذف"
                >
                  <Trash2 />
                </Button>

                <QuantitySelector
                  quantity={item.quantity}
                  onQuantityChange={(q) => updateQuantity(item.productId, item.variantId, q)}
                  className="shrink-0 scale-90"
                />

                <div className="flex-1 text-start">
                  <h4 className="line-clamp-1 text-sm font-bold">{item.productName}</h4>
                  {item.showPrice && (
                    <p className="mt-0.5 text-sm font-extrabold text-primary">{formatPrice(item.price)} DA</p>
                  )}
                </div>

                <div className="relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-muted p-1">
                  <Image
                    src={item.imageUrl || '/products/galaxy-s24-ultra.svg'}
                    alt={item.productName}
                    width={48}
                    height={48}
                    className="object-contain"
                    unoptimized
                  />
                </div>
              </div>
            ))
          )}

          {items.length > 0 && (
            <div className="border-t border-border pt-4">
              <div className="mt-4 flex items-center justify-between rounded-xl border border-border bg-muted px-4 py-3">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  <span className="text-xs font-bold">الدفع عند الاستلام</span>
                </div>
                <span className="text-[10px] text-muted-foreground">بدون دفع مسبق</span>
              </div>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="space-y-3 border-t border-border bg-muted/40 p-6">
            <div className="flex items-baseline justify-between">
              <span className="text-xs font-semibold text-muted-foreground">المجموع الكلي</span>
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-black text-primary">{formatPrice(total)}</span>
                <span className="text-xs font-bold text-muted-foreground">DA</span>
              </div>
            </div>

            <Button
              type="button"
              className="w-full gap-2.5 py-3.5 text-base font-extrabold"
              render={
                <Link href="/checkout" onClick={onClose} />
              }
            >
              <span>إتمام الطلب (Checkout)</span>
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
